import type { Color, GameState, Move, PieceState, PieceType, Square } from '@ccc/shared-types';
import type { RuleModifier } from '@ccc/rule-engine';
import {
  KNIGHT_OFFSETS,
  ORTHO_OFFSETS,
  DIAG_OFFSETS,
  emptyAt,
  enemyAt,
  fileIndex,
  fileOf,
  friendlyAt,
  indexFile,
  isOrthoAdjacent,
  lineDistance,
  makeMove,
  offsetSquare,
  pieceAt,
  piecesOfColor,
  rankOf,
  sq,
  squaresAround,
  vaultedRay,
} from './helpers.js';
import type { Offset } from './helpers.js';

const STR = (s: Square) => s;

/* ============================ PAWN ============================ */

export function pawnRelentlessMarch(color: Color): RuleModifier {
  /**
   * Pawns may advance 1 or 2 at any time. First move may be 3 squares; the pawn that
   * just moved 3 squares may not move next turn (we tag it with `relentlessCooldown`).
   * We track tagging in onMoveResolve and skip moves for tagged pieces in filterLegalMoves.
   */
  return {
    id: 'pawn-relentless-march',
    ownerSide: color,
    conflictTags: ['movement.pawn'],
    priority: 50,
    hooks: {
      extendLegalMoves(piece, state) {
        if (piece.type !== 'pawn' || piece.color !== color) return [];
        const fwd = color === 'white' ? 1 : -1;
        const extras: Move[] = [];
        // 1 or 2 squares regardless of hasMoved
        const one = offsetSquare(piece.square, 0, fwd);
        const two = offsetSquare(piece.square, 0, fwd * 2);
        if (piece.hasMoved && one && emptyAt(state, one) && two && emptyAt(state, two)) {
          extras.push(makeMove(piece, two, state));
        }
        // 3 squares on very first move
        if (!piece.hasMoved) {
          const three = offsetSquare(piece.square, 0, fwd * 3);
          if (
            one && emptyAt(state, one) &&
            two && emptyAt(state, two) &&
            three && emptyAt(state, three)
          ) {
            extras.push(makeMove(piece, three, state, { ruleTriggers: ['relentless-three'] }));
          }
        }
        return extras;
      },
      filterLegalMoves(piece, moves) {
        if (piece.color !== color) return moves;
        if (piece.customFlags['relentlessCooldown']) return [];
        return moves;
      },
      onMoveResolve(move, state) {
        const p = state.pieces[move.pieceId];
        if (!p || p.color !== color) return [];
        if (move.ruleTriggers?.includes('relentless-three')) {
          p.customFlags['relentlessCooldown'] = 2; // ticks down on each of this color's turn ends
        }
        // tick down others
        for (const other of piecesOfColor(state, color)) {
          const cd = (other.customFlags['relentlessCooldown'] as number | undefined) ?? 0;
          if (cd > 0 && other.id !== p.id) {
            other.customFlags['relentlessCooldown'] = cd - 1 > 0 ? cd - 1 : 0;
          }
        }
        return [];
      },
    },
  };
}

export function pawnRewindAndAdvance(color: Color): RuleModifier {
  /**
   * Pawns can move backward indefinitely along their file. When moving forward,
   * a pawn can retrace any prior square, plus one additional un-traversed square.
   *
   * Simplification for 1.0: we track each pawn's `pathStack` (set of squares it
   * has visited). Forward movement is allowed up to `farthestRankSeen + 1`.
   */
  return {
    id: 'pawn-rewind-and-advance',
    ownerSide: color,
    conflictTags: ['movement.pawn'],
    priority: 50,
    hooks: {
      extendLegalMoves(piece, state) {
        if (piece.type !== 'pawn' || piece.color !== color) return [];
        const fwd = color === 'white' ? 1 : -1;
        const extras: Move[] = [];
        // Backward step (no capture, just move)
        const back = offsetSquare(piece.square, 0, -fwd);
        if (back && emptyAt(state, back)) extras.push(makeMove(piece, back, state));
        // Forward up to farthestRankSeen+1
        const history = (piece.customFlags['pathStack'] as Square[] | undefined) ?? [];
        const ranks = [piece.square, ...history].map((s) => rankOf(s));
        const farthest = color === 'white' ? Math.max(...ranks) : Math.min(...ranks);
        const limit = farthest + fwd;
        // walk forward from current to limit, stopping at any blocker
        let f = 0;
        while (true) {
          f += fwd;
          if ((color === 'white' && rankOf(piece.square) + f > limit) ||
              (color === 'black' && rankOf(piece.square) + f < limit)) break;
          const t = offsetSquare(piece.square, 0, f);
          if (!t || !emptyAt(state, t)) break;
          extras.push(makeMove(piece, t, state));
        }
        return extras;
      },
      onMoveResolve(move, state) {
        const p = state.pieces[move.pieceId];
        if (!p || p.color !== color || p.type !== 'pawn') return [];
        const path = (p.customFlags['pathStack'] as Square[] | undefined) ?? [];
        path.push(move.from);
        p.customFlags['pathStack'] = path;
        return [];
      },
    },
  };
}

export function pawnFrontalAssault(color: Color): RuleModifier {
  return {
    id: 'pawn-frontal-assault',
    ownerSide: color,
    conflictTags: ['capture.pawn', 'movement.pawn', 'promotion'],
    priority: 60,
    hooks: {
      filterLegalMoves(piece, moves) {
        if (piece.color !== color || piece.type !== 'pawn') return moves;
        // Drop diagonal captures; we'll add forward capture in extend.
        return moves.filter((m) => fileOf(m.from) === fileOf(m.to));
      },
      extendLegalMoves(piece, state) {
        if (piece.color !== color || piece.type !== 'pawn') return [];
        const fwd = color === 'white' ? 1 : -1;
        const t = offsetSquare(piece.square, 0, fwd);
        if (!t) return [];
        // Capture forward if enemy directly ahead
        if (enemyAt(state, t, color)) {
          const promoRank = color === 'white' ? 7 : 2;
          if (rankOf(piece.square) === promoRank) {
            // promote early — not to queen
            return (['rook', 'bishop', 'knight'] as const).map((pt) => ({
              from: piece.square, to: t, pieceId: piece.id,
              promotionTo: pt, capturedPieceId: pieceAt(state, t)!.id,
            }));
          }
          return [makeMove(piece, t, state)];
        }
        // Early promotion option (rank 7 for white / 2 for black) without capture
        const promoRank = color === 'white' ? 7 : 2;
        if (rankOf(piece.square) === promoRank && emptyAt(state, t)) {
          return (['rook', 'bishop', 'knight'] as const).map((pt) => ({
            from: piece.square, to: t, pieceId: piece.id, promotionTo: pt,
          }));
        }
        return [];
      },
    },
  };
}

export function pawnOmnipresentPassant(color: Color): RuleModifier {
  return {
    id: 'pawn-omnipresent-passant',
    ownerSide: color,
    conflictTags: ['capture.pawn'],
    priority: 55,
    hooks: {
      extendLegalMoves(piece, state) {
        if (piece.color !== color || piece.type !== 'pawn') return [];
        const fwd = color === 'white' ? 1 : -1;
        const lastMove = state.history[state.history.length - 1];
        if (!lastMove) return [];
        const lastPiece = state.pieces[lastMove.pieceId];
        if (!lastPiece || lastPiece.color === color || lastPiece.type !== 'pawn') return [];
        // Last move was an opposing pawn advancing one rank into the same file as us, adjacent.
        if (rankOf(lastMove.to) !== rankOf(piece.square)) return [];
        if (Math.abs(fileIndex(fileOf(lastMove.to)) - fileIndex(fileOf(piece.square))) !== 1) return [];
        const target = offsetSquare(lastMove.to, 0, fwd);
        if (!target) return [];
        const promoRank = color === 'white' ? 8 : 1;
        if (rankOf(target) === promoRank) {
          return (['bishop', 'knight'] as const).map((pt) => ({
            from: piece.square, to: target, pieceId: piece.id,
            special: 'omnipresent_passant' as const,
            promotionTo: pt, capturedPieceId: lastPiece.id,
          }));
        }
        return [{
          from: piece.square, to: target, pieceId: piece.id,
          special: 'omnipresent_passant',
          capturedPieceId: lastPiece.id,
        }];
      },
    },
  };
}

export function pawnVanguardPromotion(color: Color): RuleModifier {
  return {
    id: 'pawn-vanguard-promotion',
    ownerSide: color,
    conflictTags: ['promotion'],
    priority: 50,
    hooks: {
      extendLegalMoves(piece, state) {
        if (piece.color !== color || piece.type !== 'pawn') return [];
        // Pawn is on enemy half? Offer knight-promotion in lieu of standard pushes.
        const onEnemyHalf = color === 'white' ? rankOf(piece.square) >= 5 : rankOf(piece.square) <= 4;
        if (!onEnemyHalf) return [];
        const knightCount = piecesOfColor(state, color).filter((p) => p.type === 'knight').length;
        if (knightCount >= 3) return [];
        const fwd = color === 'white' ? 1 : -1;
        const t = offsetSquare(piece.square, 0, fwd);
        if (!t || !emptyAt(state, t)) return [];
        return [{ from: piece.square, to: t, pieceId: piece.id, promotionTo: 'knight' }];
      },
      filterLegalMoves(piece, moves, state) {
        // Cap total knights at 3 for this side.
        if (piece.color !== color) return moves;
        const knights = piecesOfColor(state, color).filter((p) => p.type === 'knight').length;
        if (knights >= 3) return moves.filter((m) => m.promotionTo !== 'knight');
        return moves;
      },
    },
  };
}

export function pawnAscendantRoyalty(color: Color): RuleModifier {
  return {
    id: 'pawn-ascendant-royalty',
    ownerSide: color,
    conflictTags: ['loss-condition', 'movement.queen', 'promotion'],
    priority: 100,
    hooks: {
      overrideLossCondition(_state, c) {
        if (c !== color) return null;
        return 'queen';
      },
      extendLegalMoves(piece, state) {
        if (piece.color !== color || piece.type !== 'pawn') return [];
        // Add king promotion option when reaching back rank
        const back = color === 'white' ? 8 : 1;
        const fwd = color === 'white' ? 1 : -1;
        const t = offsetSquare(piece.square, 0, fwd);
        if (!t || rankOf(t) !== back) return [];
        if (!emptyAt(state, t) && !enemyAt(state, t, color)) return [];
        return [{ from: piece.square, to: t, pieceId: piece.id, promotionTo: 'king' as PieceType }];
      },
      filterLegalMoves(piece, moves, state) {
        // Queen restricted to 2 squares if no allied king on board
        if (piece.color !== color || piece.type !== 'queen') return moves;
        const hasKing = piecesOfColor(state, color).some((p) => p.type === 'king');
        if (hasKing) return moves;
        return moves.filter((m) => {
          const d = lineDistance(m.from, m.to);
          return d <= 2;
        });
      },
      onTurnEnd(state) {
        // Loss if queen captured for either side that has Ascendant Royalty.
        // (We only know about the side `color`, so check that side.)
        const queens = piecesOfColor(state, color).filter((p) => p.type === 'queen');
        if (queens.length === 0) {
          return { ended: true, winner: color === 'white' ? 'black' : 'white', reason: 'queen_captured' };
        }
        return null;
      },
    },
  };
}

export function pawnMiniRooks(color: Color): RuleModifier {
  /**
   * Replaces this side's pawns with mini-rooks at game-start. We do that in
   * onMoveResolve on first invocation (it runs after every move, including before
   * any if we trigger on init). Cleaner approach: card-aware setup runs once at
   * draft conclusion via setupCardEffects().
   */
  return {
    id: 'pawn-mini-rooks',
    ownerSide: color,
    conflictTags: ['starts.pawn', 'movement.pawn', 'promotion'],
    priority: 200,
    hooks: {
      filterLegalMoves(piece, moves) {
        if (piece.color !== color || piece.type !== 'mini-rook') return moves;
        // Mini-rook may not castle (it's not a king anyway, but disallow special moves)
        return moves.filter((m) => !m.special);
      },
      extendLegalMoves(piece, state) {
        // Mini-rook promotion when reaching back rank
        if (piece.color !== color || piece.type !== 'mini-rook') return [];
        const back = color === 'white' ? 8 : 1;
        if (rankOf(piece.square) !== back) return [];
        return [];
      },
      onMoveResolve(move, state) {
        // Promote mini-rook → rook on back rank
        const p = state.pieces[move.pieceId];
        if (!p || p.color !== color || p.type !== 'mini-rook') return [];
        const back = color === 'white' ? 8 : 1;
        if (rankOf(p.square) === back) p.type = 'rook';
        return [];
      },
    },
  };
}

/* ============================ KNIGHT ============================ */

export function knightDoubleTrot(color: Color): RuleModifier {
  return {
    id: 'knight-double-trot',
    ownerSide: color,
    conflictTags: ['movement.knight', 'turn.bonus'],
    priority: 50,
    hooks: {
      turnBonusGranted(move, state) {
        // After your knight's first move this turn (not a bonus already), grant a non-capturing knight bonus.
        const p = state.pieces[move.pieceId];
        if (!p || p.color !== color || p.type !== 'knight') return null;
        if (move.bonusOf !== undefined) return null;
        return {
          side: color,
          kind: 'knight_double_trot',
          canCapture: false,
          restrictToPieceIds: [p.id],
        };
      },
    },
  };
}

export function knightChillKnight(color: Color): RuleModifier {
  return {
    id: 'knight-chill-knight',
    ownerSide: color,
    conflictTags: ['movement.knight'],
    priority: 50,
    hooks: {
      extendLegalMoves(piece, state) {
        if (piece.color !== color || piece.type !== 'knight') return [];
        const extras: Move[] = [];
        for (const [df, dr] of [[2, 0], [-2, 0], [0, 2], [0, -2]] as Offset[]) {
          const t = offsetSquare(piece.square, df, dr);
          if (!t) continue;
          if (friendlyAt(state, t, color)) continue;
          extras.push(makeMove(piece, t, state));
        }
        return extras;
      },
    },
  };
}

export function knightTacticalSwap(color: Color): RuleModifier {
  return {
    id: 'knight-tactical-swap',
    ownerSide: color,
    conflictTags: ['movement.knight', 'special'],
    priority: 50,
    hooks: {
      extendLegalMoves(piece, state) {
        if (piece.color !== color || piece.type !== 'knight') return [];
        const others = piecesOfColor(state, color).filter((p) => p.id !== piece.id);
        return others.map((o) => ({
          from: piece.square,
          to: o.square,
          pieceId: piece.id,
          special: 'tactical_swap',
          ruleTriggers: ['swap:' + o.id],
        }));
      },
      onMoveResolve(move, state) {
        if (move.special !== 'tactical_swap') return [];
        const trigger = move.ruleTriggers?.find((t) => t.startsWith('swap:'));
        if (!trigger) return [];
        const otherId = trigger.slice('swap:'.length);
        const other = state.pieces[otherId];
        const knight = state.pieces[move.pieceId];
        if (!other || !knight) return [];
        const fromSq = move.from;
        other.square = fromSq;
        // knight is already on move.to (via executeMove)
        return [];
      },
    },
  };
}

export function knightShacklingAnchor(color: Color): RuleModifier {
  return {
    id: 'knight-shackling-anchor',
    ownerSide: color,
    conflictTags: ['capture.knight'],
    priority: 50,
    hooks: {
      canCapture(attacker, _target, _state) {
        if (attacker.color === color && attacker.type === 'knight') return false;
        return null;
      },
      filterLegalMoves(piece, moves, state) {
        // Filter out the moves of enemy pieces standing on a square our knight attacks.
        if (piece.color === color) return moves;
        const anchored = anchoredSquares(state, color);
        if (!anchored.has(piece.square)) return moves;
        return [];
      },
    },
  };

  function anchoredSquares(state: GameState, c: Color): Set<Square> {
    const set = new Set<Square>();
    const knights = piecesOfColor(state, c).filter((p) => p.type === 'knight');
    for (const k of knights) {
      for (const [df, dr] of KNIGHT_OFFSETS) {
        const t = offsetSquare(k.square, df, dr);
        if (t) set.add(t);
      }
    }
    return set;
  }
}

/* ============================ BISHOP ============================ */

export function bishopVaulting(color: Color): RuleModifier {
  return {
    id: 'bishop-vaulting-bishop',
    ownerSide: color,
    conflictTags: ['movement.bishop'],
    priority: 50,
    hooks: {
      extendLegalMoves(piece, state) {
        if (piece.color !== color || piece.type !== 'bishop') return [];
        return vaultedRay(piece.square, state, color, DIAG_OFFSETS).map((to) =>
          makeMove(piece, to, state, { ruleTriggers: ['vault'] }),
        );
      },
    },
  };
}

export function bishopCardinalNudge(color: Color): RuleModifier {
  return {
    id: 'bishop-cardinal-nudge',
    ownerSide: color,
    conflictTags: ['movement.bishop'],
    priority: 50,
    hooks: {
      extendLegalMoves(piece, state) {
        if (piece.color !== color || piece.type !== 'bishop') return [];
        const extras: Move[] = [];
        for (const [df, dr] of ORTHO_OFFSETS) {
          const t = offsetSquare(piece.square, df, dr);
          if (!t || friendlyAt(state, t, color)) continue;
          extras.push(makeMove(piece, t, state));
        }
        return extras;
      },
    },
  };
}

/* ============================ ROOK ============================ */

export function rookVaulting(color: Color): RuleModifier {
  return {
    id: 'rook-vaulting-rook',
    ownerSide: color,
    conflictTags: ['movement.rook'],
    priority: 50,
    hooks: {
      extendLegalMoves(piece, state) {
        if (piece.color !== color || piece.type !== 'rook') return [];
        return vaultedRay(piece.square, state, color, ORTHO_OFFSETS).map((to) =>
          makeMove(piece, to, state, { ruleTriggers: ['vault'] }),
        );
      },
    },
  };
}

export function rookLiteralFortress(color: Color): RuleModifier {
  return {
    id: 'rook-literal-fortress',
    ownerSide: color,
    conflictTags: ['capture.rook', 'turn.bonus'],
    priority: 80,
    hooks: {
      canCapture(attacker, target, _state) {
        if (target.color === color && target.type === 'rook') {
          // Only enemy king can capture
          if (attacker.type !== 'king') return false;
        }
        if (attacker.color === color && attacker.type === 'rook') {
          return false;
        }
        return null;
      },
      turnBonusGranted(move, state) {
        // Free bonus when our rook moves (once per game-turn).
        const p = state.pieces[move.pieceId];
        if (!p || p.color !== color || p.type !== 'rook') return null;
        if (move.bonusOf !== undefined) return null;
        const flag = `fortress-used-${state.moveNumber}-${color}`;
        if (state.ruleState[flag]) return null;
        state.ruleState[flag] = true;
        const rooks = piecesOfColor(state, color)
          .filter((piece) => piece.type === 'rook' && piece.id !== p.id)
          .map((piece) => piece.id);
        if (rooks.length === 0) return null;
        return {
          side: color,
          kind: 'rook_free_move',
          canCapture: false,
          restrictToPieceIds: rooks,
        };
      },
    },
  };
}

export function rookRookZone(color: Color): RuleModifier {
  return {
    id: 'rook-rook-zone',
    ownerSide: color,
    conflictTags: ['movement.all'],
    priority: 40,
    hooks: {
      extendLegalMoves(piece, state) {
        if (piece.color !== color) return [];
        const rooks = piecesOfColor(state, color).filter((p) => p.type === 'rook' && p.id !== piece.id);
        if (rooks.length === 0) return [];
        // If aligned with a friendly rook on file or rank, allow rook-style moves from current position.
        const aligned = rooks.some(
          (r) => fileOf(r.square) === fileOf(piece.square) || rankOf(r.square) === rankOf(piece.square),
        );
        if (!aligned) return [];
        const out: Move[] = [];
        for (const [df, dr] of ORTHO_OFFSETS) {
          let f = df;
          let r = dr;
          while (true) {
            const t = offsetSquare(piece.square, f, r);
            if (!t) break;
            const occ = pieceAt(state, t);
            if (!occ) out.push(makeMove(piece, t, state, { ruleTriggers: ['rook-zone'] }));
            else if (occ.color !== color) {
              out.push(makeMove(piece, t, state, { ruleTriggers: ['rook-zone'] }));
              break;
            } else break;
            f += df;
            r += dr;
          }
        }
        return out;
      },
    },
  };
}

/* ============================ QUEEN ============================ */

export function queenRoyalCavalier(color: Color): RuleModifier {
  return {
    id: 'queen-royal-cavalier',
    ownerSide: color,
    conflictTags: ['movement.queen'],
    priority: 50,
    hooks: {
      extendLegalMoves(piece, state) {
        if (piece.color !== color || piece.type !== 'queen') return [];
        const extras: Move[] = [];
        for (const [df, dr] of KNIGHT_OFFSETS) {
          const t = offsetSquare(piece.square, df, dr);
          if (!t || friendlyAt(state, t, color)) continue;
          extras.push(makeMove(piece, t, state));
        }
        return extras;
      },
      filterLegalMoves(piece, moves) {
        if (piece.color !== color || piece.type !== 'queen') return moves;
        // Linear movement capped at 6 squares; knight moves are exempt (they aren't linear).
        return moves.filter((m) => {
          const d = lineDistance(m.from, m.to);
          if (d === -1) return true; // knight-style
          return d <= 6;
        });
      },
    },
  };
}

export function queenShortRangeBurst(color: Color): RuleModifier {
  return {
    id: 'queen-short-range-burst',
    ownerSide: color,
    conflictTags: ['movement.queen'],
    priority: 70,
    hooks: {
      // Run AFTER extends to keep only moves within 2-square radius.
      filterLegalMoves(piece, moves) {
        if (piece.color !== color || piece.type !== 'queen') return moves;
        return moves.filter((m) => {
          const dfa = Math.abs(
            (m.from.charCodeAt(0) - 'a'.charCodeAt(0)) -
              (m.to.charCodeAt(0) - 'a'.charCodeAt(0)),
          );
          const dra = Math.abs(Number(m.from[1]) - Number(m.to[1]));
          return Math.max(dfa, dra) <= 2;
        });
      },
      extendLegalMoves(piece, state) {
        if (piece.color !== color || piece.type !== 'queen') return [];
        // Add squares in the 2-radius circle that the base ray gen missed
        // (e.g. blocked by intervening pieces — burst can jump them).
        return squaresAround(piece.square, 2)
          .filter((t) => !friendlyAt(state, t, color))
          .map((t) => makeMove(piece, t, state));
      },
    },
  };
}

export function queenTheMatriarchy(color: Color): RuleModifier {
  return {
    id: 'queen-the-matriarchy',
    ownerSide: color,
    conflictTags: ['starts.bishop', 'movement.king'],
    priority: 50,
    hooks: {
      filterLegalMoves(piece, moves, state) {
        if (piece.color !== color || piece.type !== 'king') return moves;
        const queens = piecesOfColor(state, color).filter((p) => p.type === 'queen');
        const adjacent = queens.some((q) => {
          const df = Math.abs(fileIndex(fileOf(q.square)) - fileIndex(fileOf(piece.square)));
          const dr = Math.abs(rankOf(q.square) - rankOf(piece.square));
          return df <= 1 && dr <= 1;
        });
        if (!adjacent) return [];
        return moves;
      },
    },
  };
}

/* ============================ KING ============================ */

export function kingTelekineticStrike(color: Color): RuleModifier {
  return {
    id: 'king-telekinetic-strike',
    ownerSide: color,
    conflictTags: ['capture.king', 'special'],
    priority: 60,
    hooks: {
      extendLegalMoves(piece, state) {
        if (piece.color !== color || piece.type !== 'king') return [];
        const out: Move[] = [];
        for (const [df, dr] of ORTHO_OFFSETS) {
          const t = offsetSquare(piece.square, df, dr);
          if (!t) continue;
          if (enemyAt(state, t, color)) {
            out.push({
              from: piece.square,
              to: piece.square, // king stays
              pieceId: piece.id,
              special: 'telekinetic_strike',
              capturedPieceId: pieceAt(state, t)!.id,
              ruleTriggers: ['telekinetic-target:' + t],
            });
          }
        }
        return out;
      },
      onMoveResolve(move, _state) {
        if (move.special !== 'telekinetic_strike') return [];
        // executeMove already deleted the captured piece by id. King didn't actually move (from === to).
        return [];
      },
    },
  };
}

export function kingPhalanxCastle(color: Color): RuleModifier {
  return {
    id: 'king-phalanx-castle',
    ownerSide: color,
    conflictTags: ['movement.king', 'movement.rook'],
    priority: 80,
    hooks: {
      onMoveResolve(move, state) {
        if (move.special === 'castle_kingside' || move.special === 'castle_queenside') {
          state.ruleState['phalanx-' + color] = { castled: true };
        }
        return [];
      },
      filterLegalMoves(piece, moves, state) {
        if (piece.color !== color) return moves;
        const phalanx = state.ruleState['phalanx-' + color] as { castled?: boolean } | undefined;
        if (!phalanx?.castled) return moves;
        if (piece.type === 'rook') {
          // Limit to max 2 squares straight, and only if king can move alongside (simplified).
          return moves.filter((m) => lineDistance(m.from, m.to) <= 2);
        }
        return moves;
      },
    },
  };
}

/* setup hooks for cards that change starting position */
export function applyStartingSetupForCard(state: GameState, cardId: string, side: Color): void {
  switch (cardId) {
    case 'pawn-mini-rooks': {
      for (const p of piecesOfColor(state, side)) {
        if (p.type === 'pawn') p.type = 'mini-rook';
      }
      break;
    }
    case 'queen-the-matriarchy': {
      // Replace king-side bishop (file f) with a queen.
      for (const p of piecesOfColor(state, side)) {
        if (p.type === 'bishop' && fileOf(p.square) === 'f') p.type = 'queen';
      }
      break;
    }
  }
}
